import * as React from 'react';
import { cn } from '@/lib/utils/classNames';

/**
 * GGC V2. Input — Figma node 132:573 family (Component 01).
 *
 * Single-line text input. Visual state is derived from native props:
 *   - `disabled`            → disabled state
 *   - `error` prop          → error state (red border)
 *   - `success` prop        → success state (green border)
 *   - native `:focus`       → focus state (blue border)
 *   - `startIcon` slot      → adds left padding for an icon (non-interactive)
 *   - `endIcon` slot        → adds right padding for an icon (interactive ok)
 *
 * Use this for new GGC V2. screens. The legacy `@/components/ui/input`
 * remains for pages not yet migrated.
 */
export interface GgcInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Visual error state (red border + ring). */
  error?: boolean;
  /** Visual success state (green border). */
  success?: boolean;
  /**
   * Visual size. `default` (38px) for forms; `sm` (30px) for dense table cells
   * and inline-edit fields.
   */
  size?: 'default' | 'sm';
  /** Icon node rendered inside the input on the left. Pointer events disabled. */
  startIcon?: React.ReactNode;
  /** Node rendered inside the input on the right (typically a button). */
  endIcon?: React.ReactNode;
}

const baseClasses =
  'w-full rounded-ggc-md border bg-white font-ggc font-normal text-ggc-main placeholder:!text-ggc-text-gray-light transition-colors focus:outline-none disabled:cursor-not-allowed';

const sizeClasses = {
  default: 'h-[38px] text-[12px]',
  sm: 'h-[30px] text-[11px]',
} as const;

const stateClasses = {
  default:
    'border-ggc-border-gray focus:border-ggc-blue focus:ring-2 focus:ring-ggc-blue/10',
  error: 'border-ggc-red ring-2 ring-ggc-red/10 focus:border-ggc-red',
  success: 'border-ggc-green focus:border-ggc-green',
  disabled: 'bg-ggc-bg-base border-ggc-disabled text-ggc-text-gray',
};

const GgcInput = React.forwardRef<HTMLInputElement, GgcInputProps>(
  (
    { className, error, success, size = 'default', startIcon, endIcon, disabled, ...props },
    ref
  ) => {
    const state = disabled
      ? 'disabled'
      : error
        ? 'error'
        : success
          ? 'success'
          : 'default';

    const sizeCls = sizeClasses[size];
    const py = size === 'sm' ? 'py-[4px]' : 'py-[9px]';
    const plNoIcon = size === 'sm' ? 'pl-[8px]' : 'pl-[12px]';
    const prNoIcon = size === 'sm' ? 'pr-[8px]' : 'pr-[12px]';

    if (!startIcon && !endIcon) {
      return (
        <input
          ref={ref}
          disabled={disabled}
          className={cn(baseClasses, sizeCls, stateClasses[state], plNoIcon, prNoIcon, py, className)}
          {...props}
        />
      );
    }

    // A bare Tailwind utility class (0,1,0) loses to the global input reset in base.css
    // (0,1,1) — use the .ggc-input-wrap/.ggc-input--icon-* class+element rules there instead,
    // which reliably win. The no-icon padding below doesn't need this since it happens to
    // already match the reset's own default.
    const padLeft = startIcon ? 'ggc-input--icon-start' : plNoIcon;
    const padRight = endIcon ? 'ggc-input--icon-end' : prNoIcon;

    return (
      <div className="ggc-input-wrap relative inline-block w-full">
        {startIcon && (
          <span className="pointer-events-none absolute left-[12px] top-1/2 flex size-[16px] -translate-y-1/2 items-center justify-center text-ggc-text-gray">
            {startIcon}
          </span>
        )}
        <input
          ref={ref}
          disabled={disabled}
          className={cn(baseClasses, sizeCls, stateClasses[state], padLeft, padRight, py, className)}
          {...props}
        />
        {endIcon && (
          <span className="absolute right-[10px] top-1/2 flex size-[20px] -translate-y-1/2 items-center justify-center text-ggc-text-gray">
            {endIcon}
          </span>
        )}
      </div>
    );
  }
);
GgcInput.displayName = 'GgcInput';

export { GgcInput };
